import React, { useState, useEffect } from 'react';
import api, { teamAPI } from '../services/api';
import { formatDateTimeMY } from '../utils/dateUtils';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { 
  Plus, 
  Loader2, 
  Edit, 
  Trash2, 
  Activity,
  Users,
  FileText,
  MessageSquare,
  UserPlus,
  UserMinus,
  RefreshCw,
  History,
  ChevronLeft,
  ChevronRight,
  Send,
  Clock,
  Repeat,
  CalendarOff,
  X
} from 'lucide-react';

const SettingsPage = () => {
  const [activeTab, setActiveTab] = useState('users');
  const [teamMembers, setTeamMembers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(null);

  // Activity logs state with pagination
  const [activityLogs, setActivityLogs] = useState([]);
  const [logsLoading, setLogsLoading] = useState(false);
  const [logsPage, setLogsPage] = useState(1);
  const [logsTotalPages, setLogsTotalPages] = useState(1);
  const [logsTotal, setLogsTotal] = useState(0);

  // Campaigns history state with pagination
  const [campaignsHistory, setCampaignsHistory] = useState([]);
  const [campaignsLoading, setCampaignsLoading] = useState(false);
  const [campaignsPage, setCampaignsPage] = useState(1);
  const [campaignsTotalPages, setCampaignsTotalPages] = useState(1);
  const [campaignsTotal, setCampaignsTotal] = useState(0);

  // WhatsApp send window
  const [sendWindow, setSendWindow] = useState({ start_hour: null, end_hour: null, send_days: [0, 1, 2, 3, 4] });
  const [sendWindowLoading, setSendWindowLoading] = useState(false);

  // Blackout dates
  const [blackoutDates, setBlackoutDates] = useState([]);
  const [newBlackoutDate, setNewBlackoutDate] = useState('');
  const [blackoutLoading, setBlackoutLoading] = useState(false);

  // Alert email
  const [alertEmail, setAlertEmail] = useState('');
  const [alertEmailLoading, setAlertEmailLoading] = useState(false);

  // Inactivity alert
  const [inactivityAlertHour, setInactivityAlertHour] = useState(14);
  const [inactivityGraceMin, setInactivityGraceMin] = useState(5);
  const [inactivityLoading, setInactivityLoading] = useState(false);

  const DAY_LABELS = [
    { value: 0, label: 'Monday' },
    { value: 1, label: 'Tuesday' },
    { value: 2, label: 'Wednesday' },
    { value: 3, label: 'Thursday' },
    { value: 4, label: 'Friday' },
    { value: 5, label: 'Saturday' },
    { value: 6, label: 'Sunday' },
  ];

  const toggleDay = (dayValue) => {
    const days = sendWindow.send_days || [];
    if (days.includes(dayValue)) {
      setSendWindow({ ...sendWindow, send_days: days.filter(d => d !== dayValue) });
    } else {
      setSendWindow({ ...sendWindow, send_days: [...days, dayValue].sort() });
    }
  };

  // Form state
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    role: 'user'
  });

  useEffect(() => {
    fetchTeamMembers();
  }, []);

  useEffect(() => {
    if (activeTab === 'activity') {
      fetchActivityLogs(logsPage);
    }
  }, [activeTab, logsPage]);

  useEffect(() => {
    if (activeTab === 'campaigns') {
      fetchCampaignsHistory(campaignsPage);
    }
  }, [activeTab, campaignsPage]);

  useEffect(() => {
    if (activeTab === 'whatsapp') {
      fetchSendWindow();
      fetchBlackoutDates();
      fetchAlertEmail();
      fetchInactivitySettings();
    }
  }, [activeTab]);

  const fetchSendWindow = async () => {
    try {
      const res = await api.get('/settings/wa-send-window');
      setSendWindow(res.data);
    } catch (e) {}
  };

  const saveSendWindow = async () => {
    setSendWindowLoading(true);
    try {
      await api.put('/settings/wa-send-window', sendWindow);
      alert('Send window saved!');
    } catch (e) {
      alert('Failed to save');
    } finally {
      setSendWindowLoading(false);
    }
  };

  const fetchBlackoutDates = async () => {
    try {
      const res = await api.get('/settings/wa-blackout-dates');
      setBlackoutDates(res.data.dates || []);
    } catch (e) {}
  };

  const addBlackoutDate = async () => {
    if (!newBlackoutDate) return;
    if (blackoutDates.includes(newBlackoutDate)) {
      setNewBlackoutDate('');
      return;
    }
    const updated = [...blackoutDates, newBlackoutDate].sort();
    setBlackoutLoading(true);
    try {
      await api.put('/settings/wa-blackout-dates', { dates: updated });
      setBlackoutDates(updated);
      setNewBlackoutDate('');
    } catch (e) {
      alert('Failed to save');
    } finally {
      setBlackoutLoading(false);
    }
  };

  const removeBlackoutDate = async (date) => {
    const updated = blackoutDates.filter(d => d !== date);
    setBlackoutLoading(true);
    try {
      await api.put('/settings/wa-blackout-dates', { dates: updated });
      setBlackoutDates(updated);
    } catch (e) {
      alert('Failed to save');
    } finally {
      setBlackoutLoading(false);
    }
  };

  const fetchAlertEmail = async () => {
    try {
      const res = await api.get('/settings/wa-alert-email');
      setAlertEmail(res.data.email || '');
    } catch {}
  };

  const saveAlertEmail = async () => {
    setAlertEmailLoading(true);
    try {
      await api.put('/settings/wa-alert-email', { email: alertEmail });
      alert('Alert email saved!');
    } catch (e) {
      alert('Failed to save');
    } finally {
      setAlertEmailLoading(false);
    }
  };

  const fetchInactivitySettings = async () => {
    try {
      const res = await api.get('/settings/wa-inactivity-alert');
      setInactivityAlertHour(res.data.alert_hour ?? 14);
      setInactivityGraceMin(res.data.campaign_grace_minutes ?? 5);
    } catch {}
  };

  const saveInactivitySettings = async () => {
    setInactivityLoading(true);
    try {
      await api.put('/settings/wa-inactivity-alert', { alert_hour: inactivityAlertHour, campaign_grace_minutes: inactivityGraceMin });
      alert('Inactivity alert settings saved!');
    } catch (e) {
      alert('Failed to save');
    } finally {
      setInactivityLoading(false);
    }
  };


  const fetchTeamMembers = async () => {
    try {
      const response = await teamAPI.getAll();
      setTeamMembers(response.data);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  const fetchActivityLogs = async (page = 1) => {
    setLogsLoading(true);
    try {
      const response = await api.get(`/activity-logs?page=${page}&per_page=20`);
      setActivityLogs(response.data.logs || []);
      setLogsTotalPages(response.data.total_pages || 1);
      setLogsTotal(response.data.total || 0);
    } catch (error) {
    } finally {
      setLogsLoading(false);
    }
  };

  const fetchCampaignsHistory = async (page = 1) => {
    setCampaignsLoading(true);
    try {
      const response = await api.get(`/wa/campaigns-history?page=${page}&per_page=20`);
      setCampaignsHistory(response.data.campaigns || []);
      setCampaignsTotalPages(response.data.total_pages || 1);
      setCampaignsTotal(response.data.total || 0);
    } catch (error) {
    } finally {
      setCampaignsLoading(false);
    }
  };

  const openCreateModal = () => {
    setEditingUser(null);
    setFormData({ name: '', email: '', password: '', role: 'user' });
    setIsModalOpen(true);
  };

  const openEditModal = (user) => {
    setEditingUser(user);
    setFormData({ name: user.name, email: user.email, password: '', role: user.role });
    setIsModalOpen(true);
  };

  const handleSaveUser = async () => {
    setSaving(true);
    try {
      if (editingUser) {
        const updateData = { name: formData.name, email: formData.email, role: formData.role };
        if (formData.password) updateData.password = formData.password;
        await api.put(`/users/${editingUser.id}`, updateData);
      } else {
        await teamAPI.create(formData);
      }
      setIsModalOpen(false);
      fetchTeamMembers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to save user');
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUser = async (user) => {
    if (!window.confirm(`Are you sure you want to delete user "${user.name}"?`)) return;
    setDeleting(user.id);
    try {
      await api.delete(`/users/${user.id}`);
      fetchTeamMembers();
    } catch (error) {
      alert(error.response?.data?.detail || 'Failed to delete user');
    } finally {
      setDeleting(null);
    }
  };

  const getRoleColor = (role) => {
    switch (role?.toLowerCase()) {
      case 'admin': return 'bg-red-100 text-red-700';
      case 'manager': return 'bg-purple-100 text-purple-700';
      case 'support': return 'bg-blue-100 text-blue-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getActivityIcon = (entityType, actionType) => {
    if (entityType === 'user') {
      if (actionType === 'create') return <UserPlus size={16} className="text-green-500" />;
      if (actionType === 'delete') return <UserMinus size={16} className="text-red-500" />;
      return <Users size={16} className="text-blue-500" />;
    }
    if (entityType === 'client') {
      if (actionType === 'create') return <UserPlus size={16} className="text-green-500" />;
      if (actionType === 'delete') return <UserMinus size={16} className="text-red-500" />;
      return <Users size={16} className="text-blue-500" />;
    }
    if (entityType === 'template') return <FileText size={16} className="text-purple-500" />;
    if (entityType === 'campaign') return <MessageSquare size={16} className="text-indigo-500" />;
    return <Activity size={16} className="text-gray-500" />;
  };

  const getActionTypeColor = (actionType) => {
    switch (actionType) {
      case 'create': return 'bg-green-100 text-green-700';
      case 'update': return 'bg-blue-100 text-blue-700';
      case 'delete': return 'bg-red-100 text-red-700';
      case 'action': return 'bg-purple-100 text-purple-700';
      default: return 'bg-gray-100 text-gray-600';
    }
  };

  const getCampaignTypeIcon = (type) => {
    if (type === 'scheduled') return <Clock size={14} className="text-blue-600" />;
    return <Send size={14} className="text-green-600" />;
  };

  const PaginationControls = ({ page, totalPages, total, onPageChange, label }) => {
    if (totalPages <= 1) return null;
    return (
      <div className="flex items-center justify-between px-6 py-3 border-t border-gray-100" data-testid={`pagination-${label}`}>
        <p className="text-sm text-gray-500">
          Page {page} of {totalPages} ({total} total)
        </p>
        <div className="flex items-center gap-2">
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page - 1)}
            disabled={page <= 1}
            data-testid={`prev-page-${label}`}
          >
            <ChevronLeft size={14} className="mr-1" /> Previous
          </Button>
          <Button
            variant="outline"
            size="sm"
            onClick={() => onPageChange(page + 1)}
            disabled={page >= totalPages}
            data-testid={`next-page-${label}`}
          >
            Next <ChevronRight size={14} className="ml-1" />
          </Button>
        </div>
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="animate-spin text-[#4F46E5]" size={32} />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="settings-page">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Settings</h1>
        <p className="text-gray-500">Manage your CRM configuration</p>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex gap-4">
          {[
            { key: 'users', label: 'User Management', icon: Users },
            { key: 'whatsapp', label: 'WhatsApp', icon: Send },
            { key: 'activity', label: 'Activity Log', icon: Activity },
            { key: 'campaigns', label: 'Campaigns History', icon: History },
          ].map(({ key, label, icon: Icon }) => (
            <button
              key={key}
              onClick={() => setActiveTab(key)}
              data-testid={`tab-${key}`}
              className={`pb-3 px-1 font-medium text-sm border-b-2 transition-colors ${
                activeTab === key
                  ? 'border-[#4F46E5] text-[#4F46E5]'
                  : 'border-transparent text-gray-500 hover:text-gray-700'
              }`}
            >
              <Icon size={16} className="inline mr-2" />
              {label}
            </button>
          ))}
        </nav>
      </div>

      {/* WhatsApp Settings Tab */}
      {activeTab === 'whatsapp' && (
        <>
        <Card className="bg-white border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900">Fixed Flow Send Window</h2>
            <p className="text-gray-500 text-sm">
              Control when fixed flow messages appear in Today's Queue. Messages will only be visible during this window (MYT).
              Scheduled campaigns are not affected by this setting.
            </p>
          </div>
          <div className="p-6 space-y-4">
            <div className="grid grid-cols-2 gap-4 max-w-md">
              <div>
                <Label className="text-sm font-medium text-gray-700">Start After (MYT)</Label>
                <select
                  value={sendWindow.start_hour ?? ''}
                  onChange={(e) => setSendWindow({ ...sendWindow, start_hour: e.target.value ? parseInt(e.target.value) : null })}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]"
                  data-testid="send-window-start"
                >
                  <option value="">No restriction</option>
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}</option>
                  ))}
                </select>
              </div>
              <div>
                <Label className="text-sm font-medium text-gray-700">End Before (MYT)</Label>
                <select
                  value={sendWindow.end_hour ?? ''}
                  onChange={(e) => setSendWindow({ ...sendWindow, end_hour: e.target.value ? parseInt(e.target.value) : null })}
                  className="w-full mt-1 px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#4F46E5]"
                  data-testid="send-window-end"
                >
                  <option value="">No restriction</option>
                  {Array.from({ length: 24 }, (_, i) => (
                    <option key={i} value={i}>{i === 0 ? '12:00 AM' : i < 12 ? `${i}:00 AM` : i === 12 ? '12:00 PM' : `${i - 12}:00 PM`}</option>
                  ))}
                </select>
              </div>
            </div>
            
            {/* Send Days */}
            <div>
              <Label className="text-sm font-medium text-gray-700 mb-2 block">Send Days</Label>
              <div className="flex flex-wrap gap-2">
                {DAY_LABELS.map(({ value, label }) => (
                  <label
                    key={value}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg border cursor-pointer transition-colors ${
                      (sendWindow.send_days || []).includes(value)
                        ? 'bg-[#4F46E5]/10 border-[#4F46E5] text-[#4F46E5]'
                        : 'bg-white border-gray-200 text-gray-500 hover:border-gray-300'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={(sendWindow.send_days || []).includes(value)}
                      onChange={() => toggleDay(value)}
                      className="rounded text-[#4F46E5]"
                    />
                    <span className="text-sm font-medium">{label}</span>
                  </label>
                ))}
              </div>
            </div>
            
            <p className="text-xs text-gray-400">
              Example: Start 1:00 PM, End 9:00 PM, Mon-Fri — Fixed flow messages only show in queue between 1pm and 9pm MYT on weekdays.
            </p>
            <Button
              onClick={saveSendWindow}
              disabled={sendWindowLoading}
              className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
              data-testid="save-send-window"
            >
              {sendWindowLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Clock size={16} className="mr-2" />}
              Save Send Window
            </Button>
          </div>
        </Card>

        {/* Blackout Dates */}
        <Card className="bg-white border border-gray-100 mt-6">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <CalendarOff size={20} className="text-red-500" /> Blackout Dates
            </h2>
            <p className="text-gray-500 text-sm">
              Skip all broadcasts on specific dates (e.g. public holidays, double-date sales). Fixed flows and scheduled campaigns will be paused on these days.
            </p>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex gap-3 max-w-md">
              <Input
                type="date"
                value={newBlackoutDate}
                onChange={(e) => setNewBlackoutDate(e.target.value)}
                className="flex-1"
                data-testid="blackout-date-input"
              />
              <Button
                onClick={addBlackoutDate}
                disabled={blackoutLoading || !newBlackoutDate}
                className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
                data-testid="add-blackout-date-btn"
              >
                {blackoutLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Plus size={16} className="mr-2" />}
                Add Date
              </Button>
            </div>

            {blackoutDates.length === 0 ? (
              <p className="text-sm text-gray-400">No blackout dates set. Broadcasts will run on all scheduled days.</p>
            ) : (
              <div className="flex flex-wrap gap-2" data-testid="blackout-dates-list">
                {blackoutDates.map((date) => {
                  const d = new Date(date + 'T00:00:00');
                  const label = d.toLocaleDateString('en-MY', { day: 'numeric', month: 'short', year: 'numeric', weekday: 'short' });
                  const isPast = date < new Date().toISOString().slice(0, 10);
                  return (
                    <div
                      key={date}
                      className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-lg border text-sm ${
                        isPast ? 'bg-gray-50 border-gray-200 text-gray-400' : 'bg-red-50 border-red-200 text-red-700'
                      }`}
                      data-testid={`blackout-date-${date}`}
                    >
                      <CalendarOff size={14} />
                      <span className="font-medium">{label}</span>
                      <button
                        onClick={() => removeBlackoutDate(date)}
                        className="hover:bg-red-100 rounded-full p-0.5 transition-colors"
                        data-testid={`remove-blackout-${date}`}
                      >
                        <X size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}

            <p className="text-xs text-gray-400">
              Tip: Add dates like 8.8, 9.9, 10.10 for double-date sales, or public holidays like Hari Raya, Chinese New Year, etc.
            </p>
          </div>
        </Card>

        {/* Alert Email */}
        <Card className="bg-white border border-gray-100 mt-6">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <MessageSquare size={20} className="text-amber-500" /> Alert Emails
            </h2>
            <p className="text-gray-500 text-sm">
              When a message is skipped with reason "WhatsApp disconnected", the campaign will be auto-paused and an alert email sent. A summary email is also sent when campaigns complete.
            </p>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex gap-3 max-w-lg">
              <Input
                type="text"
                placeholder="e.g. edward@company.com, team@company.com"
                value={alertEmail}
                onChange={(e) => setAlertEmail(e.target.value)}
                className="flex-1"
                data-testid="alert-email-input"
              />
              <Button
                onClick={saveAlertEmail}
                disabled={alertEmailLoading}
                className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
                data-testid="save-alert-email-btn"
              >
                {alertEmailLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : <Clock size={16} className="mr-2" />}
                Save
              </Button>
            </div>
            <p className="text-xs text-gray-400">
              Separate multiple emails with commas. Leave empty to disable alerts.
            </p>
          </div>
        </Card>

        {/* Broadcast Inactivity Alert */}
        <Card className="bg-white border border-gray-100 mt-6">
          <div className="p-6 border-b border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 flex items-center gap-2">
              <Clock size={20} className="text-red-500" /> Broadcast Inactivity Alert
            </h2>
            <p className="text-gray-500 text-sm">
              Get an email alert if broadcasts have pending messages but no one is sending them.
            </p>
          </div>
          <div className="p-6 space-y-4">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700 font-medium whitespace-nowrap">Fixed Flow alert after:</label>
                <select
                  value={inactivityAlertHour}
                  onChange={(e) => setInactivityAlertHour(parseInt(e.target.value))}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-32"
                  data-testid="inactivity-alert-hour"
                >
                  {Array.from({length: 13}, (_, i) => i + 8).map(h => (
                    <option key={h} value={h}>{String(h).padStart(2, '0')}:00 MYT</option>
                  ))}
                </select>
              </div>
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700 font-medium whitespace-nowrap">Campaign alert after:</label>
                <select
                  value={inactivityGraceMin}
                  onChange={(e) => setInactivityGraceMin(parseInt(e.target.value))}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm w-32"
                  data-testid="inactivity-grace-min"
                >
                  <option value={2}>2 minutes</option>
                  <option value={5}>5 minutes</option>
                  <option value={10}>10 minutes</option>
                  <option value={15}>15 minutes</option>
                  <option value={30}>30 minutes</option>
                </select>
              </div>
              <Button
                onClick={saveInactivitySettings}
                disabled={inactivityLoading}
                className="bg-[#4F46E5] hover:bg-[#4338CA] text-white"
                data-testid="save-inactivity-btn"
              >
                {inactivityLoading ? <Loader2 size={16} className="animate-spin mr-2" /> : null}
                Save
              </Button>
            </div>
            <p className="text-xs text-gray-400">
              Fixed Flows: alert once per day if today's queue has pending messages but none sent by the configured hour.
              Campaigns: alert once if running but no messages sent within the grace period after activation.
            </p>
          </div>
        </Card>
        </>
      )}

      {/* User Management Tab */}
      {activeTab === 'users' && (
        <Card className="bg-white border border-gray-100">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">User Management</h2>
                <p className="text-gray-500 text-sm">Manage team members and their access levels</p>
              </div>
              <Button onClick={openCreateModal} className="bg-[#4F46E5] hover:bg-[#4338CA] text-white h-10">
                <Plus size={16} className="mr-2" /> Add User
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Name</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Email</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Role</th>
                  <th className="px-6 py-3 text-left text-xs font-semibold text-gray-500 uppercase tracking-wider">Created</th>
                  <th className="px-6 py-3 text-right text-xs font-semibold text-gray-500 uppercase tracking-wider">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {teamMembers.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50 transition-colors">
                    <td className="px-6 py-4 text-sm font-medium text-gray-900">{member.name}</td>
                    <td className="px-6 py-4 text-sm text-gray-600">{member.email}</td>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 text-xs font-medium rounded-full ${getRoleColor(member.role)}`}>{member.role}</span>
                    </td>
                    <td className="px-6 py-4 text-sm text-gray-500">{formatDateTimeMY(member.created_at)}</td>
                    <td className="px-6 py-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <Button size="sm" variant="outline" onClick={() => openEditModal(member)} className="text-blue-600 border-blue-200 hover:bg-blue-50">
                          <Edit size={14} className="mr-1" /> Edit
                        </Button>
                        <Button size="sm" variant="outline" onClick={() => handleDeleteUser(member)} disabled={deleting === member.id} className="text-red-600 border-red-200 hover:bg-red-50">
                          {deleting === member.id ? <Loader2 size={14} className="animate-spin mr-1" /> : <Trash2 size={14} className="mr-1" />}
                          Delete
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {teamMembers.length === 0 && (
            <div className="p-8 text-center"><p className="text-gray-500">No team members found</p></div>
          )}
        </Card>
      )}

      {/* Activity Log Tab */}
      {activeTab === 'activity' && (
        <Card className="bg-white border border-gray-100" data-testid="activity-log-card">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Activity Log</h2>
                <p className="text-gray-500 text-sm">Track all actions in the CRM system ({logsTotal} entries)</p>
              </div>
              <Button variant="outline" onClick={() => fetchActivityLogs(logsPage)} disabled={logsLoading}>
                <RefreshCw size={16} className={`mr-2 ${logsLoading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
          </div>

          {logsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-[#4F46E5]" size={32} />
            </div>
          ) : activityLogs.length === 0 ? (
            <div className="p-12 text-center">
              <Activity className="mx-auto text-gray-300 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Activity Yet</h3>
              <p className="text-gray-500">Actions like creating templates, starting campaigns, and managing clients will appear here.</p>
            </div>
          ) : (
            <div className="divide-y divide-gray-100">
              {activityLogs.map((log) => (
                <div key={log.id} className="p-4 hover:bg-gray-50">
                  <div className="flex items-start gap-4">
                    <div className="mt-1">{getActivityIcon(log.entity_type, log.action_type)}</div>
                    <div className="flex-1">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="font-medium text-gray-900">{log.description}</span>
                        <span className={`px-2 py-0.5 text-xs rounded-full ${getActionTypeColor(log.action_type)}`}>{log.action_type}</span>
                      </div>
                      <div className="flex items-center gap-4 text-sm text-gray-500">
                        <span>by {log.user_name}</span>
                        <span>&bull;</span>
                        <span>{formatDateTimeMY(log.created_at)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <PaginationControls
            page={logsPage}
            totalPages={logsTotalPages}
            total={logsTotal}
            onPageChange={setLogsPage}
            label="activity-logs"
          />
        </Card>
      )}

      {/* Campaigns History Tab */}
      {activeTab === 'campaigns' && (
        <Card className="bg-white border border-gray-100" data-testid="campaigns-history-card">
          <div className="p-6 border-b border-gray-100">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-lg font-semibold text-gray-900">Campaigns History</h2>
                <p className="text-gray-500 text-sm">Completed WhatsApp campaigns ({campaignsTotal} total)</p>
              </div>
              <Button variant="outline" onClick={() => fetchCampaignsHistory(campaignsPage)} disabled={campaignsLoading}>
                <RefreshCw size={16} className={`mr-2 ${campaignsLoading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
          </div>

          {campaignsLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="animate-spin text-[#4F46E5]" size={32} />
            </div>
          ) : campaignsHistory.length === 0 ? (
            <div className="p-12 text-center">
              <History className="mx-auto text-gray-300 mb-4" size={48} />
              <h3 className="text-lg font-medium text-gray-900 mb-2">No Completed Campaigns</h3>
              <p className="text-gray-500">Completed WhatsApp campaigns will appear here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="bg-gray-50">
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Campaign</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Type</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Stats</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-gray-500 uppercase">Completed</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {campaignsHistory.map((campaign) => (
                    <tr key={campaign.id} className="hover:bg-gray-50" data-testid={`history-row-${campaign.id}`}>
                      <td className="px-4 py-4">
                        <p className="font-medium text-gray-900">{campaign.name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">Created: {formatDateTimeMY(campaign.created_at)}</p>
                      </td>
                      <td className="px-4 py-4">
                        <span className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium rounded-full bg-gray-50 text-gray-700 border border-gray-200">
                          {getCampaignTypeIcon(campaign.type)} {campaign.type === 'scheduled' ? 'Scheduled' : 'Immediate'}
                        </span>
                      </td>
                      <td className="px-4 py-4">
                        {campaign.stats ? (
                          <div className="text-sm">
                            <span className="text-green-600 font-medium">{campaign.stats.sent} sent</span>
                            {campaign.stats.failed > 0 && <span className="text-red-500 ml-2">{campaign.stats.failed} failed</span>}
                            {campaign.stats.skipped > 0 && <span className="text-gray-400 ml-2">{campaign.stats.skipped} skipped</span>}
                            <span className="text-gray-400 ml-2">/ {campaign.stats.total} total</span>
                          </div>
                        ) : (
                          <span className="text-sm text-gray-400">-</span>
                        )}
                      </td>
                      <td className="px-4 py-4 text-sm text-gray-500">
                        {campaign.completed_at ? formatDateTimeMY(campaign.completed_at) : '-'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          <PaginationControls
            page={campaignsPage}
            totalPages={campaignsTotalPages}
            total={campaignsTotal}
            onPageChange={setCampaignsPage}
            label="campaigns-history"
          />
        </Card>
      )}

      {/* Add/Edit User Modal */}
      <Dialog open={isModalOpen} onOpenChange={setIsModalOpen}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>{editingUser ? 'Edit User' : 'Add New User'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Name</Label>
              <Input placeholder="John Doe" value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input type="email" placeholder="john@example.com" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>{editingUser ? 'New Password (leave blank to keep current)' : 'Password'}</Label>
              <Input type="password" placeholder="********" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={formData.role} onValueChange={(value) => setFormData({ ...formData, role: value })}>
                <SelectTrigger><SelectValue placeholder="Select role" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="manager">Manager</SelectItem>
                  <SelectItem value="support">Customer Support</SelectItem>
                  <SelectItem value="user">User</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <Button
              onClick={handleSaveUser}
              className="w-full bg-[#4F46E5] hover:bg-[#4338CA] text-white"
              disabled={!formData.name || !formData.email || (!editingUser && !formData.password) || saving}
            >
              {saving ? <Loader2 className="animate-spin mr-2" size={16} /> : null}
              {editingUser ? 'Save Changes' : 'Create User'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default SettingsPage;
